import { Modal, SelectProps, FormControl, Select, MenuItem, createStyles, makeStyles, Theme, Button, InputLabel } from "@material-ui/core"
import React, { ChangeEvent, useContext, useState } from "react"
import FormatListNumbered from '@material-ui/icons/FormatListNumbered';
import { useTranslation } from "react-i18next";
import {OrderContext} from "../OrderContext";

type ReturnContextState = { open: boolean, setOpen: React.Dispatch<boolean> }

const ReturnReasonSelectorContext = React.createContext<ReturnContextState>({} as ReturnContextState)

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 350,
            backgroundColor: theme.palette.background.paper,
            border: '2px solid #000',
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
        },
        icon: {
            marginRight: '1.75rem',
            padding: '.5rem',
            fontSize: '2.75rem',
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.primary.main,
            borderRadius: '50%'
        },
        contactOption: {
            display: 'flex',
            flexDirection: 'row',
            margin: '30px 0 30px'
        },
        contactText: {
            margin: 'auto auto auto 0',
            fontSize: '1.05rem'
        },
        errorText: {
            color: theme.palette.error.main,
            marginTop: '.3rem',
            fontSize: '.75rem'
        }
    }),
)

export const ReturnReasonSelectorContextProvider: React.FC = (props) => {
    const [open, setOpen] = useState(false)
    const classes = useStyles()
    const { t } = useTranslation()
    const {customer} = useContext(OrderContext)
    
    const locale = navigator.language?.split('-')
    const country = (customer.country || (locale?.length > 1 ? locale[1] : 'international')).toLocaleLowerCase()
    const language = (locale?.length ? locale[0] : 'en').toLocaleLowerCase()

    return (
        <ReturnReasonSelectorContext.Provider value={{ open, setOpen }}>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="product-complaint-modal-title"
                aria-describedby="product-complaint-modal-description"
            >
                <div className={classes.paper}>
                    <h2 id="product-complaint-modal-title">{t('contact_customer_support')}</h2>
                    <p id="product-complaint-modal-description">
                        {t('address_product_compliant')}
                    </p>

                    <div className={classes.contactOption}>
                        <FormatListNumbered className={classes.icon} />
                        <a href={`https://store.nobelbiocare.com/${country}/${language}/complaint`} className={classes.contactText} target="_blank" rel="noreferrer">{t('product_compliant_form')}</a>
                    </div>

                    <Button color='primary' variant='contained' onClick={() => setOpen(false)} style={{ float: 'right' }}>
                        {t('dismiss')}
                    </Button>
                </div>
            </Modal>
            {props.children}
        </ReturnReasonSelectorContext.Provider>
    )
}

export const ReturnReasonSelector: React.FC<SelectProps> = ({ onChange, label, ...props }) => {
    const { setOpen } = useContext(ReturnReasonSelectorContext)
    const {returnsEnabled, exchangesEnabled} = useContext(OrderContext)
    const classes = useStyles()
    const { t } = useTranslation()

    const handleChange = (e: ChangeEvent<{ name?: string, value: unknown }>, child: React.ReactNode) => {
        if (e.target.value === 'Product Complaint') setOpen(true)
        if (onChange) onChange(e, child)
    }

    const error = props.value === 'Product Complaint'
    
    const getReturnExchangeTranslationKey = () => {
        const r = returnsEnabled ? '1': '0'
        const e = exchangesEnabled ? '1' : '0'
        const re = r + e
        switch (re){
            case '10':
                return 'return_reason_only_return'
            case '01':
                return 'return_reason_only_exchange'
            default:
                return 'return_exchange'
        }
    }

    return (
        <FormControl variant="outlined" style={{ width: '100%' }} size='small'>
            <InputLabel>{label}</InputLabel>

            <Select {...props} label={label} onChange={handleChange} style={{ width: '100%' }} error={error || props.value === '_'}>
                <MenuItem value='_'>{t('select_a_reason')}...</MenuItem>
                <MenuItem value='Return Exchange'>{t(getReturnExchangeTranslationKey())}</MenuItem>
                <MenuItem value='Product Complaint'>{t('product_compliant')}</MenuItem>
            </Select>

            {error && props.value !== '_' && <span className={classes.errorText}>{t('invalid_return_reason')}</span>}
        </FormControl>
    )
}
